import { select } from "redux-saga/effects";

import { TripStatus, TripType } from "@mapmycustomers/shared";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { Trip } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import User from "@mapmycustomers/shared/types/User";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import DistanceTravelledCardConfiguration, {
  DistanceTravelledAggregationType,
} from "@app/types/dashboard/cards/distanceTravelled/DistanceTravelledCardConfiguration";
import DistanceTravelledCardData from "@app/types/dashboard/cards/distanceTravelled/DistanceTravelledCardData";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import { parseApiDate } from "@app/util/parsers";

import { Granularity } from "../common";

interface DistanceTravelledAggregationItem {
  doc_count: number;
  key: number; // date in numeric format
  key_as_string: string; // date in string format
  rank: number;
  sum_distance: { value: number };
  without_distance: { value: number };
}

const buildTripFilters = (range: DateRange, isOwnUserScope: boolean, userIds: User["id"][]) => ({
  status: TripStatus.COMPLETED,
  // when fetching for other users, only fetch business trips
  ...(isOwnUserScope ? {} : { type: TripType.BUSINESS }),
  $or: [
    { startTime: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() } },
    { endTime: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() } },
  ],
  userId: userIds.length ? { [PlatformFilterOperator.CONTAINS]: userIds } : undefined,
});

const toHistory = (
  aggregations: DistanceTravelledAggregationItem[],
  aggregationType: DistanceTravelledAggregationType
): NonNullable<DistanceTravelledCardData["history"]> =>
  aggregations.map((item) => ({
    date: parseApiDate(item.key),
    distance:
      aggregationType === DistanceTravelledAggregationType.SUM
        ? (item.sum_distance.value ?? 0)
        : item.doc_count
          ? Math.round(item.sum_distance.value / item.doc_count)
          : 0,
  }));

// Both ranges run the same histogram so the trend compares like with like: in AVG mode the
// card's value is a sum of per-bucket averages, which an ungrouped total would not match.
function* fetchTripHistory(params: {
  aggregationType: DistanceTravelledAggregationType;
  granularity: Granularity;
  isOwnUserScope: boolean;
  orgId: Organization["id"];
  range: DateRange;
  userIds: User["id"][];
}): Generator<unknown, NonNullable<DistanceTravelledCardData["history"]>, never> {
  const { aggregationType, granularity, isOwnUserScope, orgId, range, userIds } = params;
  const payload = {
    $aggs: {
      primaryGroup: {
        field: "startTime",
        interval: granularity,
        offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
        timezone: getLocalTimeZone(),
      },
      sum: ["distance"],
    },
    $filters: buildTripFilters(range, isOwnUserScope, userIds),
    $limit: 0,
  };

  const response: AggregatedListResponse<Trip, DistanceTravelledAggregationItem[]> = yield callApi(
    "fetchTrips",
    orgId,
    payload
  );
  return toHistory(response.aggregations, aggregationType);
}

export function* fetchDistanceTravelledFromTrips(params: {
  configuration: DistanceTravelledCardConfiguration;
  granularity: Granularity;
  isOwnUserScope: boolean;
  userIds: User["id"][];
}): Generator<unknown, DistanceTravelledCardData, never> {
  const { configuration, granularity, isOwnUserScope, userIds } = params;
  const { criteria, dateRange, preferences } = configuration;
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const aggregationType = criteria.aggregationType;

  const history = yield* fetchTripHistory({
    aggregationType,
    granularity,
    isOwnUserScope,
    orgId,
    range: getDatesRange(dateRange.range, dateRange.subRange),
    userIds,
  });

  let runningTotal = 0;
  const cumulativeHistory = history.map((item) => {
    runningTotal += item.distance;
    return { ...item, distance: runningTotal };
  });

  const previousHistory = preferences.showTrend
    ? yield* fetchTripHistory({
        aggregationType,
        granularity,
        isOwnUserScope,
        orgId,
        range: getDatesRange(dateRange.range, dateRange.subRange, true),
        userIds,
      })
    : undefined;

  return {
    distance: history.reduce((sum, item) => sum + item.distance, 0),
    history: preferences.showChart
      ? preferences.cumulative
        ? cumulativeHistory
        : history
      : undefined,
    previousDistance: previousHistory?.reduce((sum, item) => sum + item.distance, 0),
  };
}

export default fetchDistanceTravelledFromTrips;
